import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  AppBar,
  Toolbar,
  Dialog,
  DialogContent,
  Chip,
} from "@mui/material";
import {
  PlayArrow as PlayIcon,
  ArrowUpward as HigherIcon,
  ArrowDownward as LowerIcon,
  DragHandle as TieIcon,
  Refresh as RefreshIcon,
  VolumeUp as ClueIcon,
  Person as SoloIcon,
  Groups as LiveIcon,
  LocalFireDepartment as HotSeatIcon,
} from "@mui/icons-material";
import UserAuthButton from "../auth/UserAuthButton";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../../context/AuthContext";
import HigherOrLowerSpUser from "./HigherOrLowerSpUser";
import useGameSounds from "../../../hooks/useGameSounds";
import SoundMuteButton from "../../Common/SoundMuteButton";
import { resolveClueNotes, playClueNote } from "../../../utils/audioClue";
import GameOverResultsModal from "../../games/GameOverResultsModal";
import FlippableCard from "../../games/FlippableCard";
import {
  HOL_CARD_FLIP_URL,
  HOL_HOT_SEAT_CLAP_URL,
  playHolSound,
  stopHolAudio,
} from "../../../constants/holDefaultSounds";
import {
  HOL,
  holAppBarSx,
  holCardSlotSx,
  holCardsBoardSx,
  holCardsGridSx,
  holClueBtnSx,
  holControlsBarSx,
  holFadeUp,
  holGhostBtnSx,
  holHigherBtnSx,
  holLowerBtnSx,
  holTieBtnSx,
  holModeTileSx,
  holPageSx,
  holPrimaryBtnSx,
  holPulse,
} from "./holTheme";
import useGameHistory from "../../hooks/useGameHistory";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const HigherOrLower = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, isLoading } = useAuthContext();
  const searchParams = new URLSearchParams(location.search);
  const forceJoin = searchParams.get("forceJoin") === "1";
  const [playMode, setPlayMode] = useState("pick");
  const { goTo: goToGameScreen, goBack: goBackGameScreen } = useGameHistory({
    game: "higher_or_lower",
    onScreen: (screen, data) => {
      if (forceJoin) return;
      if (screen === "home" || screen === "mode") {
        setPlayMode("pick");
        return;
      }
      if (screen === "game" && data?.playMode) {
        setPlayMode(data.playMode);
      }
    },
  });

  useEffect(() => {
    if (isLoading) return;
    const sp = new URLSearchParams(location.search).get("mode");
    if (sp === "sp" || forceJoin) setPlayMode("single_player");
  }, [isLoading, location.search, forceJoin]);
  const [gameStarted, setGameStarted] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [cards, setCards] = useState([]);
  const [revealedCards, setRevealedCards] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [lastResult, setLastResult] = useState(null); // 'correct' or 'wrong'
  const [showResult, setShowResult] = useState(false);
  const [flippingCardIndex, setFlippingCardIndex] = useState(null);
  const [resultsModalOpen, setResultsModalOpen] = useState(false);
  const { play, stopBgMusic, sounds } = useGameSounds("higher_or_lower");
  const playRef = useRef(play);
  const soundsRef = useRef(sounds);
  const cluePairRef = useRef({ cardIndex: -1, reference: null, second: null });
  const clapAudioRef = useRef(null);

  useEffect(() => {
    playRef.current = play;
  }, [play]);

  useEffect(() => {
    soundsRef.current = sounds;
  }, [sounds]);

  useEffect(() => {
    if (lastResult === "correct") playRef.current("correct");
    else if (lastResult === "wrong") playRef.current("wrong");
  }, [lastResult]);

  const stopGameOverClaps = () => {
    stopHolAudio(clapAudioRef.current);
    clapAudioRef.current = null;
  };

  const closeResultsModal = () => {
    setResultsModalOpen(false);
    stopGameOverClaps();
  };

  useEffect(() => {
    if (!gameOver) return undefined;
    setResultsModalOpen(true);
    stopBgMusic();
    stopHolAudio(clapAudioRef.current);
    clapAudioRef.current = playHolSound(
      soundsRef.current,
      null,
      "hot_seat",
      HOL_HOT_SEAT_CLAP_URL,
      { loop: false, forceDirect: true },
    );
    return () => {
      stopHolAudio(clapAudioRef.current);
      clapAudioRef.current = null;
    };
  }, [gameOver]); // eslint-disable-line react-hooks/exhaustive-deps -- start claps once when game ends

  // Tracking state
  const [sessionId, setSessionId] = useState(null);
  const [gameStartTime, setGameStartTime] = useState(null);
  const [roundStartTime, setRoundStartTime] = useState(null);

  // Configuration
  const [numberOfCards, setNumberOfCards] = useState(5); // Default to 5
  const isGigMode = ["1", "true", "yes"].includes(
    (new URLSearchParams(location.search).get("gigmode") || "").toLowerCase(),
  );

  // Get device ID
  const getDeviceId = () => {
    let deviceId = localStorage.getItem("device_id");
    if (!deviceId) {
      deviceId =
        "device_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
      localStorage.setItem("device_id", deviceId);
    }
    return deviceId;
  };

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem("auth_token");
  };

  // Card values: 2–10 < Jack < Queen < King < Ace < Joker (highest)
  const cardValues = [
    { name: "Joker", displayName: "Joker", fileValue: "joker", value: 15, isJokerType: true },
    { name: "Ace", displayName: "Ace", fileValue: "1", value: 14 },
    { name: "King", displayName: "King", fileValue: "king", value: 13 },
    { name: "Queen", displayName: "Queen", fileValue: "queen", value: 12 },
    { name: "Jack", displayName: "Jack", fileValue: "jack", value: 11 },
    { name: "10", displayName: "10", fileValue: "10", value: 10 },
    { name: "9", displayName: "9", fileValue: "9", value: 9 },
    { name: "8", displayName: "8", fileValue: "8", value: 8 },
    { name: "7", displayName: "7", fileValue: "7", value: 7 },
    { name: "6", displayName: "6", fileValue: "6", value: 6 },
    { name: "5", displayName: "5", fileValue: "5", value: 5 },
    { name: "4", displayName: "4", fileValue: "4", value: 4 },
    { name: "3", displayName: "3", fileValue: "3", value: 3 },
    { name: "2", displayName: "2", fileValue: "2", value: 2 },
  ];

  // Suits for regular cards
  const suits = ["club", "diamond", "heart", "spade"];

  // Generate unique cards from a shuffled 54-card deck (52 suited + 2 jokers)
  const generateCards = () => {
    const fullDeck = [];
    for (const value of cardValues) {
      if (value.isJokerType) {
        fullDeck.push({ ...value, isJoker: true, jokerColor: "red" });
        fullDeck.push({ ...value, isJoker: true, jokerColor: "black" });
      } else {
        for (const suit of suits) {
          fullDeck.push({ ...value, suit });
        }
      }
    }

    for (let i = fullDeck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [fullDeck[i], fullDeck[j]] = [fullDeck[j], fullDeck[i]];
    }

    const n = Math.min(numberOfCards, fullDeck.length);
    return fullDeck.slice(0, n).map(({ isJokerType, ...card }) => card);
  };

  // Fetch configuration
  const fetchConfiguration = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/settings/public/higher-or-lower-cards`,
      );
      if (response.ok) {
        const data = await response.json();
        const cardsCount = data.numberOfCards || 5;
        setNumberOfCards(cardsCount);
      } else {
        // Use default if fetch fails
        setNumberOfCards(5);
      }
    } catch (error) {
      console.error("Error fetching configuration:", error);
      // Use default 5 if fetch fails
      setNumberOfCards(5);
    }
  };

  // Initialize game
  const initializeGame = () => {
    stopGameOverClaps();
    setResultsModalOpen(false);
    const cardsCount = numberOfCards || 5; // Ensure we have a valid number
    const newCards = generateCards();
    setCards(newCards);
    setRevealedCards(new Array(cardsCount).fill(false));
    setCurrentCardIndex(0);
    setGameStarted(false);
    setGameOver(false);
    setScore(0);
    setLastResult(null);
    setShowResult(false);
    setFlippingCardIndex(null);
    setSessionId(null);
    setGameStartTime(null);
    setRoundStartTime(null);
  };

  const redirectToMymForAuth = () => {
    if (window.top !== window) {
      window.top.location.href = `${window.location.origin}/mindyourmusic?openAuth=1&returnTo=higher_or_lower`;
    } else {
      navigate("/mindyourmusic", { state: { openAuth: true, returnTo: "higher_or_lower" } });
    }
  };

  // Start game - reveal first card
  const handleStart = async () => {
    if (!isAuthenticated || !user) {
      redirectToMymForAuth();
      return;
    }

    try {
      // Create session
      const deviceId = getDeviceId();
      const response = await fetch(`${API_BASE}/api/higher-or-lower/sessions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
          "x-device-id": deviceId,
        },
        body: JSON.stringify({
          total_cards: numberOfCards,
          guest_name: user?.guest_name,
        }),
      });

      if (!response.ok) throw new Error("Failed to create session");

      const data = await response.json();
      setSessionId(data.session_id);
      setGameStartTime(Date.now());

      // Record cards
      await fetch(
        `${API_BASE}/api/higher-or-lower/sessions/${data.session_id}/cards`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({ cards: cards }),
        },
      );

      const newRevealed = [...revealedCards];
      newRevealed[0] = true;
      setRevealedCards(newRevealed);
      setGameStarted(true);
      setRoundStartTime(Date.now());
      playHolSound(soundsRef.current, playRef.current, "card_flip", HOL_CARD_FLIP_URL);
    } catch (error) {
      console.error("Error starting game:", error);
      // Continue with game even if tracking fails
      const newRevealed = [...revealedCards];
      newRevealed[0] = true;
      setRevealedCards(newRevealed);
      setGameStarted(true);
      setRoundStartTime(Date.now());
      playHolSound(soundsRef.current, playRef.current, "card_flip", HOL_CARD_FLIP_URL);
    }
  };

  // Handle Higher / Lower / Tie guess
  const handleGuess = async (guess) => {
    if (currentCardIndex >= cards.length - 1) return;
    if (guess !== "higher" && guess !== "lower" && guess !== "tie") return;

    const currentCard = cards[currentCardIndex];
    const nextCard = cards[currentCardIndex + 1];

    // Calculate response time
    const responseTime = roundStartTime ? Date.now() - roundStartTime : null;

    // Start flip animation
    setFlippingCardIndex(currentCardIndex + 1);
    playHolSound(soundsRef.current, playRef.current, "card_flip", HOL_CARD_FLIP_URL);

    // After flip animation completes, reveal the card
    setTimeout(async () => {
      const newRevealed = [...revealedCards];
      newRevealed[currentCardIndex + 1] = true;
      setRevealedCards(newRevealed);
      setFlippingCardIndex(null);

      const isTieResult = nextCard.value === currentCard.value;
      let isCorrect = false;
      let isTie = false;
      let result = "wrong";

      if (guess === "tie") {
        isCorrect = isTieResult;
        isTie = isTieResult;
        result = isCorrect ? "correct" : "wrong";
      } else if (isTieResult) {
        // Equal values but player didn't call tie — push (no score change)
        isCorrect = false;
        isTie = true;
        result = "tie";
      } else if (guess === "higher") {
        isCorrect = nextCard.value > currentCard.value;
        result = isCorrect ? "correct" : "wrong";
      } else {
        isCorrect = nextCard.value < currentCard.value;
        result = isCorrect ? "correct" : "wrong";
      }

      const nextScore = isCorrect ? score + 1 : score;
      if (isCorrect) setScore(nextScore);
      setLastResult(result);
      setShowResult(true);

      // Record round
      if (sessionId) {
        try {
          await fetch(
            `${API_BASE}/api/higher-or-lower/sessions/${sessionId}/rounds`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${getAuthToken()}`,
              },
              body: JSON.stringify({
                round_number: currentCardIndex + 1,
                current_card_value: currentCard.value,
                current_card_name: currentCard.displayName,
                current_card_suit: currentCard.suit,
                current_card_is_joker: currentCard.isJoker ? 1 : 0,
                next_card_value: nextCard.value,
                next_card_name: nextCard.displayName,
                next_card_suit: nextCard.suit,
                next_card_is_joker: nextCard.isJoker ? 1 : 0,
                user_guess: guess,
                is_correct: isCorrect ? 1 : 0,
                is_tie: isTie ? 1 : 0,
                response_time_ms: responseTime,
              }),
            },
          );
        } catch (error) {
          console.error("Error recording round:", error);
        }
      }

      // Move to next card or end game
      setTimeout(() => {
        if (currentCardIndex + 1 === cards.length - 1) {
          endGame(nextScore);
        } else {
          setCurrentCardIndex(currentCardIndex + 1);
          setShowResult(false);
          setLastResult(null);
          setRoundStartTime(Date.now());
        }
      }, 1000);
    }, 300); // Half of flip animation duration
  };

  // End game and update session
  const endGame = async (finalScore = score) => {
    setGameOver(true);

    if (sessionId && gameStartTime) {
      try {
        const duration = Math.floor((Date.now() - gameStartTime) / 1000);

        await fetch(`${API_BASE}/api/higher-or-lower/sessions/${sessionId}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({
            final_score: finalScore,
            total_rounds: cards.length - 1,
            game_status: "completed",
            duration_seconds: duration,
          }),
        });
      } catch (error) {
        console.error("Error updating session:", error);
      }
    }
  };

  // Clue: only latest visible + its next hidden card are active
  const canUseClue =
    gameStarted &&
    !gameOver &&
    !showResult &&
    flippingCardIndex === null &&
    currentCardIndex < cards.length - 1;

  const isClueButtonActive = (index) =>
    canUseClue &&
    (index === currentCardIndex || index === currentCardIndex + 1);

  const shouldShowClueButton = (index) => {
    if (!gameStarted || gameOver) return false;
    // Previous revealed, latest visible, or immediate next hidden
    if (index <= currentCardIndex) return true;
    if (index === currentCardIndex + 1) return true;
    return false;
  };

  const handleClue = (index) => {
    if (!canUseClue) return;
    const currentCard = cards[currentCardIndex];
    const hiddenCard = cards[currentCardIndex + 1];

    if (cluePairRef.current.cardIndex !== currentCardIndex) {
      const resolved = resolveClueNotes(currentCard, hiddenCard);
      if (!resolved) return;
      cluePairRef.current = {
        cardIndex: currentCardIndex,
        reference: resolved.reference,
        second: resolved.second,
      };
    }

    const { reference, second } = cluePairRef.current;
    if (index === currentCardIndex) {
      playClueNote(reference, soundsRef.current);
    } else if (index === currentCardIndex + 1) {
      playClueNote(second, soundsRef.current);
    }
  };

  // Require auth after mode is chosen (not on mode picker screen)
  useEffect(() => {
    if (isLoading) return;
    if (playMode === "pick") return;
    if (!isAuthenticated || !user) {
      if (window.top !== window) {
        window.top.location.href = `${window.location.origin}/mindyourmusic?openAuth=1&returnTo=higher_or_lower`;
      } else {
        navigate("/mindyourmusic", { state: { openAuth: true, returnTo: "higher_or_lower" } });
      }
    }
  }, [isLoading, isAuthenticated, user, navigate, playMode]);

  // Fetch configuration on mount
  useEffect(() => {
    fetchConfiguration();
  }, []);

  // Initialize (or re-initialize) offline game whenever numberOfCards is set/changed
  useEffect(() => {
    if (playMode !== "offline") return;
    if (numberOfCards > 0) {
      initializeGame();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOfCards, playMode]);

  return (
    <Box sx={holPageSx}>
      {!isGigMode && (
        <AppBar position="static" sx={holAppBarSx}>
          <Toolbar sx={{ position: "relative", minHeight: { xs: 48, sm: 56 }, px: { xs: 1, sm: 2 } }}>
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", zIndex: 1 }}>
              <Box
                component="img"
                src="/assets/higherorlowerLogo.png"
                alt="Higher or Lower"
                role={!forceJoin && playMode !== "pick" ? "button" : undefined}
                tabIndex={!forceJoin && playMode !== "pick" ? 0 : undefined}
                onClick={() => {
                  if (forceJoin || playMode === "pick") return;
                  goBackGameScreen();
                }}
                onKeyDown={(e) => {
                  if (forceJoin || playMode === "pick") return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    goBackGameScreen();
                  }
                }}
                sx={{
                  height: { xs: 28, sm: 40 },
                  objectFit: "contain",
                  cursor: !forceJoin && playMode !== "pick" ? "pointer" : "default",
                  filter: "drop-shadow(0 0 12px rgba(168,224,99,0.25))",
                }}
              />
            </Box>

            <Typography
              component="div"
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                fontFamily: HOL.fontDisplay,
                fontWeight: 800,
                letterSpacing: "0.04em",
                whiteSpace: "nowrap",
                pointerEvents: "none",
                fontSize: { xs: "0.75rem", sm: "0.95rem" },
                background: `linear-gradient(90deg, ${HOL.higher}, ${HOL.gold}, ${HOL.lower})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                display: { xs: "none", sm: "block" },
              }}
            >
              HIGHER OR LOWER
            </Typography>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 0.5,
                zIndex: 1,
              }}
            >
              <SoundMuteButton />
              <UserAuthButton />
            </Box>
          </Toolbar>
        </AppBar>
      )}
      {isGigMode && <SoundMuteButton floating />}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          zIndex: 1,
          overflow: "hidden",
          width: "100%",
        }}
      >
        {playMode === "pick" && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: 1,
            }}
          >
            {/* <Box
              component="img"
              src="/assets/higherorlowerLogo.png"
              alt="Higher or Lower"
              sx={{
                width: { xs: 200, sm: 260, md: 300 },
                maxWidth: "78vw",
                height: "auto",
                mb: { xs: 2, sm: 2.5 },
                animation: `${holFloat} 4.5s ease-in-out infinite`,
                filter: "drop-shadow(0 12px 40px rgba(168,224,99,0.2))",
              }}
            /> */}
            <Typography
              sx={{
                fontFamily: HOL.fontDisplay,
                fontWeight: 800,
                fontSize: { xs: "1.65rem", sm: "2.1rem", md: "2.4rem" },
                letterSpacing: "-0.02em",
                mb: 1,
                animation: `${holFadeUp} 0.5s ease both`,
              }}
            >
              Pick your night
            </Typography>
            <Typography
              sx={{
                color: HOL.muted,
                maxWidth: 420,
                mb: { xs: 3, sm: 4 },
                fontSize: { xs: "0.95rem", sm: "1.05rem" },
                lineHeight: 1.55,
                animation: `${holFadeUp} 0.55s 0.06s ease both`,
              }}
            >
              Chart instinct. One call. Higher or lower — how bold are you feeling?
            </Typography>

            <Grid
              container
              spacing={2}
              justifyContent="center"
              sx={{ maxWidth: 720, mx: "auto", width: "100%" }}
            >
              <Grid item xs={12} sm={6}>
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setPlayMode("offline");
                    goToGameScreen("game", { playMode: "offline" });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setPlayMode("offline");
                      goToGameScreen("game", { playMode: "offline" });
                    }
                  }}
                  sx={{ ...holModeTileSx(HOL.higher), animationDelay: "0.12s" }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      background: "rgba(168,224,99,0.12)",
                      color: HOL.higher,
                    }}
                  >
                    <SoloIcon />
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: HOL.fontDisplay,
                      fontWeight: 800,
                      fontSize: "1.35rem",
                      mb: 0.75,
                    }}
                  >
                    Play Solo
                  </Typography>
                  <Typography sx={{ color: HOL.muted, fontSize: "0.92rem", lineHeight: 1.5, mb: 2 }}>
                  Play Higher or Lower at your own pace. Just you, the cards and your winning streak.
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: HOL.fontDisplay,
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: HOL.higher,
                    }}
                  >
                    Play solo →
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setPlayMode("single_player");
                    goToGameScreen("game", { playMode: "single_player" });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setPlayMode("single_player");
                      goToGameScreen("game", { playMode: "single_player" });
                    }
                  }}
                  sx={{ ...holModeTileSx(HOL.lower), animationDelay: "0.2s" }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      background: "rgba(255,107,61,0.12)",
                      color: HOL.lower,
                    }}
                  >
                    <LiveIcon />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                    <Typography
                      sx={{
                        fontFamily: HOL.fontDisplay,
                        fontWeight: 800,
                        fontSize: "1.35rem",
                      }}
                    >
                      Play With Host
                    </Typography>
                    <Chip
                      size="small"
                      icon={<HotSeatIcon sx={{ fontSize: "14px !important" }} />}
                      label="LIVE"
                      sx={{
                        height: 22,
                        fontWeight: 800,
                        fontSize: "0.65rem",
                        letterSpacing: "0.06em",
                        bgcolor: "rgba(255,107,61,0.2)",
                        color: HOL.lower,
                        border: `1px solid ${HOL.lower}55`,
                        "& .MuiChip-icon": { color: HOL.lower },
                        animation: `${holPulse} 2.2s ease-in-out infinite`,
                      }}
                    />
                  </Box>
                  <Typography sx={{ color: HOL.muted, fontSize: "0.92rem", lineHeight: 1.5, mb: 2 }}>
                  Join the live game. The host calls the shots, puts players in the Hot Seat and reveals the cards - Win real Prizes !!!
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: HOL.fontDisplay,
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: HOL.lower,
                    }}
                  >
                    Join the room →
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        <Box sx={{ flex: playMode === "pick" ? 0 : 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>

          {playMode === "single_player" && isAuthenticated && user && (
            <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <HigherOrLowerSpUser
              getDeviceId={getDeviceId}
              getAuthToken={getAuthToken}
              user={user}
              forceJoin={forceJoin}
              onBack={() => goBackGameScreen()}
            />
            </Box>
          )}

          {playMode === "offline" && (
            <GameOverResultsModal
              open={resultsModalOpen}
              onClose={closeResultsModal}
              score={score}
              outOf={Math.max(0, (cards.length || 1) - 1)}
              actionLabel="Play Again"
              onAction={initializeGame}
            />
          )}

        {/* Cards Grid — offline only */}
        {playMode === "offline" && (
        <Box sx={holCardsBoardSx}>
          <Box sx={holCardsGridSx(cards.length)}>
          {cards.map((cardValue, index) => (
            <Box
              key={index}
              sx={{
                ...holCardSlotSx(cards.length),
                transform:
                  flippingCardIndex === index || revealedCards[index]
                    ? "scale(1)"
                    : "scale(0.94)",
                transition: "transform 0.3s ease",
              }}
            >
                  <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
                  <FlippableCard
                    card={cardValue}
                    revealed={flippingCardIndex === index || !!revealedCards[index]}
                    maxHeight="100%"
                    minHeight={0}
                    alt={cardValue.displayName}
                  />
                  </Box>

                  {shouldShowClueButton(index) && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ClueIcon sx={{ fontSize: { xs: 12, sm: 16 } }} />}
                      onClick={() => handleClue(index)}
                      disabled={!isClueButtonActive(index)}
                      sx={{
                        ...holClueBtnSx(isClueButtonActive(index)),
                        flexShrink: 0,
                        "& .MuiButton-startIcon": { mr: { xs: 0, sm: 0.5 } },
                        fontSize: { xs: 0, sm: "0.75rem" },
                        minWidth: { xs: 28, sm: "auto" },
                        width: { xs: 28, sm: "auto" },
                        px: { xs: 0.5, sm: 1 },
                      }}
                      aria-label="Clue"
                    >
                      <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                        Clue
                      </Box>
                    </Button>
                  )}
            </Box>
          ))}
          </Box>
        </Box>
        )}

      {/* Game Controls — offline only */}
      {playMode === "offline" && (
      <Box
        sx={{
          textAlign: "center",
          ...holControlsBarSx,
        }}
      >
        {!gameStarted ? (
          <Button
            variant="contained"
            size="large"
            startIcon={<PlayIcon />}
            onClick={handleStart}
            sx={holPrimaryBtnSx}
          >
            Flip the first card
          </Button>
        ) : !gameOver ? (
          <Box sx={{ maxWidth: { xs: 520, sm: 560 }, mx: "auto" }}>
            <Dialog
              open={!!(showResult && lastResult)}
              onClose={() => {}}
              PaperProps={{
                sx: {
                  borderRadius: "18px",
                  p: 0,
                  overflow: "hidden",
                  minWidth: 220,
                  background:
                    lastResult === "correct"
                      ? "linear-gradient(160deg, #1a2e14 0%, #0d160a 100%)"
                      : lastResult === "tie"
                        ? "linear-gradient(160deg, #1a1a22 0%, #0e0e14 100%)"
                        : "linear-gradient(160deg, #2a1410 0%, #140a08 100%)",
                  border: `1px solid ${
                    lastResult === "correct"
                      ? `${HOL.higher}55`
                      : lastResult === "tie"
                        ? "rgba(255,255,255,0.15)"
                        : `${HOL.lower}55`
                  }`,
                },
              }}
              BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,0.55)" } }}
            >
              <DialogContent
                sx={{
                  py: 3.5,
                  px: 4,
                  textAlign: "center",
                  fontFamily: HOL.fontDisplay,
                  fontWeight: 800,
                  fontSize: "1.35rem",
                  letterSpacing: "0.04em",
                  color:
                    lastResult === "correct"
                      ? HOL.higher
                      : lastResult === "tie"
                        ? HOL.gold
                        : HOL.lower,
                }}
              >
                {lastResult === "correct"
                  ? "Nailed it"
                  : lastResult === "tie"
                    ? "Same value — push"
                    : "Not this time"}
              </DialogContent>
            </Dialog>
            <Typography
              sx={{
                mb: { xs: 0.75, sm: 1 },
                fontSize: { xs: "0.82rem", sm: "0.95rem" },
                color: HOL.muted,
                px: { xs: 0.5, sm: 0 },
                fontWeight: 500,
              }}
            >
              Higher, lower, or tie vs{" "}
              <Box
                component="span"
                sx={{
                  color: "#fff",
                  fontFamily: HOL.fontDisplay,
                  fontWeight: 800,
                }}
              >
                {cards[currentCardIndex]?.displayName}
              </Box>
              ?
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: { xs: 1, sm: 1.5 },
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<HigherIcon />}
                onClick={() => handleGuess("higher")}
                disabled={showResult || flippingCardIndex !== null}
                sx={holHigherBtnSx}
                aria-label="Higher"
              >
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  Higher
                </Box>
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={<TieIcon />}
                onClick={() => handleGuess("tie")}
                disabled={showResult || flippingCardIndex !== null}
                sx={holTieBtnSx}
                aria-label="Tie"
              >
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  Tie
                </Box>
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={<LowerIcon />}
                onClick={() => handleGuess("lower")}
                disabled={showResult || flippingCardIndex !== null}
                sx={holLowerBtnSx}
                aria-label="Lower"
              >
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  Lower
                </Box>
              </Button>
              <Button
                variant="outlined"
                onClick={initializeGame}
                sx={{
                  ...holGhostBtnSx,
                  minWidth: { xs: 36, sm: 48 },
                  width: { xs: 36, sm: "auto" },
                  px: { xs: 0.75, sm: 1.5 },
                  minHeight: { xs: 36, sm: 50 },
                }}
                aria-label="Restart"
              >
                <RefreshIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
              </Button>
            </Box>
          </Box>
        ) : (
          <Button
            variant="contained"
            size="large"
            startIcon={<RefreshIcon />}
            onClick={initializeGame}
            sx={holPrimaryBtnSx}
          >
            Play Again
          </Button>
        )}

      </Box>
      )}
        </Box>
      </Box>
    </Box>
  );
};

export default HigherOrLower;
